import React, { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { CameraAlt, Visibility, Upload } from '@mui/icons-material';
import SMSModal from './SMSModal';
import ImageLightbox from './ImageLightBox';
import { FileUploader } from 'react-drag-drop-files';
import useIsMobile from '@hooks/useIsMobile';

type Props = {
  label?: string;
  frontPhoto?: any;
  backPhoto?: any;
  onUploadFront: (file: File) => void;
  onUploadBack: (file: File) => void;
  onOpenSMS: () => any;
};

const ImageUploadField = ({
  label,
  frontPhoto,
  backPhoto,
  onUploadFront,
  onUploadBack,
  onOpenSMS,
}: Props) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);
  const isMobile = useIsMobile();

  const handleOpenLightbox = (index: number) => {
    setLightboxStartIndex(index);
    setLightboxOpen(true);
  };

  const renderImageBox = (
    image: string | undefined,
    label: string,
    index: number
  ) => {
    const handleUpload = (file: File) => {
      index === 0 ? onUploadFront(file) : onUploadBack(file);
    };
  
    const hasImage = Boolean(image);
  
    if (!hasImage) {
      return (
        <Box sx={{ flex: 1, minWidth: '50%' }}>
          <FileUploader
            handleChange={handleUpload}
            name={label.toLowerCase()}
            types={['JPG', 'JPEG', 'PNG']}
            hoverTitle=""
            children={
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '5 / 3',
                  border: '1px dashed #1147EA',
                  borderRadius: 2,
                  backgroundColor: '#f4f6fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 0.5,
                  cursor: 'pointer',
                }}
              >
                <Typography 
                  variant="body2"
                  sx={{ 
                    color: '#666', 
                    px: '32px',
                    '@media (max-width:767px)': {
                      px: '8px',
                    }, 
                    }}
                  >
                  Tap to upload <strong>{label}</strong> photo
                </Typography>
              </Box>
            }
          />
        </Box>
      );
    }
  
    return (
      <Box sx={{ flex: 1, minWidth: '50%', position: 'relative' }}>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '5 / 3',
            border: '1px dashed #1147EA',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: '#f4f6fa',
          }}
        >
          <img
            src={image}
            loading="lazy"
            alt={`${label} of insurance card`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              '@media (max-width:767px)': {
                gap: 1,
              }, 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
            }}
          >
            <Box
              onClick={() => handleOpenLightbox(index)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.5,
                paddingTop: '16px',
                width: '100%',
                cursor: 'pointer',
                color: '#2D1583',
                fontWeight: 500,
                fontSize: '14px',
                '@media (max-width:767px)': {
                  fontSize: '12px',
                }, 
                transition: 'transform 0.3s ease, opacity 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Visibility />
              View
            </Box>
    
            <FileUploader
              handleChange={handleUpload}
              name={`${label.toLowerCase()}-replace`}
              types={['JPG', 'JPEG', 'PNG']}
              hoverTitle=""
              children={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.5,
                    paddingBottom: '16px',
                    width: '100%',
                    cursor: 'pointer',
                    color: '#2D1583',
                    fontWeight: 500,
                    fontSize: '14px',
                    '@media (max-width:767px)': {
                      fontSize: '12px',
                    }, 
                    transition: 'transform 0.3s ease, opacity 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Upload />
                  Replace Image
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>
    );    
  };

  return (
    <Box
      className="manual-upload-section"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        mt: 2,
        mb: 1,
      }}
    >
      <Typography variant="body2">{label}</Typography>

      <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
        {renderImageBox(frontPhoto, 'FRONT', 0)}
        {renderImageBox(backPhoto, 'BACK', 1)}
      </Box>

      {!isMobile && (
        <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onOpenSMS}
              sx={{ py: '8px', textTransform: 'none' }}
            >
              <CameraAlt sx={{ color: '#FFFFFF', mr: 0.5 }} />
              Upload Using Mobile Device
            </Button>
          </Box>
        </Box>
      )}

      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={[frontPhoto, backPhoto]}
        startIndex={lightboxStartIndex}
      />
    </Box>
  );
};

export default ImageUploadField;